import { newStepValue } from '../../../../../services/flowBuilder/utils/constants';
import { getEditTypes } from '../../../delegatorUtils';
import { UntypedFormArray, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { EditPermissions } from '../../../../../../../shared/models';
import {
  hasDuplicates,
  hasNegativeNumbers,
  maxLength,
  minimumCount,
  required,
} from '../../../../../../../shared/utils';

interface Results {
  next: string;
  description: string;
}

interface OtpData {
  retries: number;
  resends: number;
  otpLength: number;
  otpTimetolive: number;
  resendInput: string[];
  otpMessageText: string;
  resendLimitReachedMessage: string;
  retryText: string;
  resendText: string;
  text: string;
  successNext: string;
  successDescription: string;
  failureNext: string;
  failureDescription: string;
  errorNext: string;
  errorDescription: string;
}

export class OtpNode {

  menu: {} = {};

  constructor() {
    this.menu = {
      'menuType': 'otp',
    };
  }

  public static toForm(stepData: { [index: string]: any }, editPermissions: EditPermissions): UntypedFormGroup {
    const { editMenuTextOnly, editFlows } = getEditTypes(editPermissions);
    return this.buildForm(this.getNodeData(stepData), editFlows);
  }

  private static buildForm(nodeData: OtpData, editFlows: boolean): UntypedFormGroup {
    return new UntypedFormGroup({
      retries: new UntypedFormControl({
        value: nodeData.retries,
        disabled: !editFlows,
      }, [required('Retries'), hasNegativeNumbers(), minimumCount(1, 'Retries')]),
      resends: new UntypedFormControl({
        value: nodeData.resends,
        disabled: !editFlows,
      }, [required('Resends'), hasNegativeNumbers(), minimumCount(1, 'Resends')]),
      otpLength: new UntypedFormControl({
        value: nodeData.otpLength,
        disabled: !editFlows,
      }, [required('Otp length'), hasNegativeNumbers(), minimumCount(4, 'Otp length')]),
      otpTimetolive: new UntypedFormControl({
        value: nodeData.otpTimetolive,
        disabled: !editFlows,
      }, [required('Time out'), hasNegativeNumbers()]),
      resendInput: this.setResendInputs(nodeData.resendInput),
      otpMessageText: new UntypedFormControl({
        value: nodeData.otpMessageText,
        disabled: !editFlows,
      }, required('OTP message text')),
      resendLimitReachedMessage: new UntypedFormControl({
        value: nodeData.resendLimitReachedMessage,
        disabled: !editFlows,
      }, required('Resend limit reached message')),
      retryText: new UntypedFormControl({
        value: nodeData.retryText,
        disabled: !editFlows,
      }, required('Retry text')),
      resendText: new UntypedFormControl({
        value: nodeData.resendText,
        disabled: !editFlows,
      }, required('Resend Text')),
      text: new UntypedFormControl({
        value: nodeData.text,
        disabled: !editFlows,
      }, required('Menu Header')),
      successNext: new UntypedFormControl({
        value: nodeData.successNext,
        disabled: !editFlows,
      }),
      successDescription: new UntypedFormControl({
        value: nodeData.successDescription,
        disabled: !editFlows,
      }, required('Description')),
      failureNext: new UntypedFormControl({
        value: nodeData.failureNext,
        disabled: !editFlows,
      }),
      failureDescription: new UntypedFormControl({
        value: nodeData.failureDescription,
        disabled: !editFlows,
      }, required('Description')),
      errorNext: new UntypedFormControl({
        value: nodeData.errorNext,
        disabled: !editFlows,
      }),
      errorDescription: new UntypedFormControl({
        value: nodeData.errorDescription,
        disabled: !editFlows,
      }, required('Description')),
    });
  }

  private static setResendInputs(resendInputs: string[]) {
    const resendInputsControl = new UntypedFormArray([], [maxLength(5), hasDuplicates()]);

    for (const resendInput of resendInputs) {
      resendInputsControl.push(new UntypedFormControl(resendInput));
    }

    return resendInputsControl;
  }


  private static getNodeData(stepData: { [index: string]: any }): OtpData {
    const menu = stepData?.menu ? stepData?.menu : {};
    const text = menu.text ? menu.text : '';
    const otp = menu.otp ? menu.otp : {};

    const {
      retries,
      resends,
      otpLength,
      otpTimetolive,
      resendInputs,
      otpMessageText,
      resendLimitReachedMessage,
      retryText,
      resendText,
    } = otp;
    const results = menu.results ? menu.results : {};

    const success: Results = results.success ? results.success : {};
    const failure: Results = results.failure ? results.failure : {};
    const error: Results = results.error ? results.error : {};

    return {
      text,
      retries: retries ? retries : 3,
      resends: resends ? resends : 2,
      otpLength: otpLength ? otpLength : 4,
      otpTimetolive: otpTimetolive ? otpTimetolive : 0,
      resendInput: resendInputs ? resendInputs : [],
      otpMessageText: otpMessageText ? otpMessageText : '',
      resendLimitReachedMessage: resendLimitReachedMessage ? resendLimitReachedMessage : '',
      retryText: retryText ? retryText : '',
      resendText: resendText ? resendText : '',
      successNext: success.next ? success.next : newStepValue,
      successDescription: success.description ? success.description : 'Success',
      errorNext: error.next ? error.next : newStepValue,
      errorDescription: error.description ? error.description : 'Error',
      failureNext: failure.next ? failure.next : newStepValue,
      failureDescription: failure.description ? failure.description : 'Failure',
    };
  }

  setMenuData(otpData: OtpData): void {
    const { text } = otpData;

    this.menu = {
      ...this.menu,
      text,
    };

    this.setOtp(otpData);
    this.setResults(otpData);
  }

  private setOtp(otpData: OtpData) {
    const {
      retries,
      resends,
      otpLength,
      otpTimetolive,
      resendInput,
      otpMessageText,
      resendLimitReachedMessage,
      retryText,
      resendText,
    } = otpData;

    const otp = {
      retries,
      resends,
      otpLength,
      otpTimetolive,
      resendInputs: resendInput,
      otpMessageText,
      resendLimitReachedMessage,
      retryText,
      resendText,
    };

    this.menu = {
      ...this.menu,
      otp,
    };
  }

  private setResults(otpData: OtpData) {
    const {
      successNext,
      successDescription,
      failureNext,
      failureDescription,
      errorNext,
      errorDescription,
    } = otpData;

    const success: Results = {
      next: successNext,
      description: successDescription,
    };

    const failure: Results = {
      next: failureNext,
      description: failureDescription,
    };

    const error: Results = {
      next: errorNext,
      description: errorDescription,
    };

    const results = {
      success,
      failure,
      error,
    };

    this.menu = {
      ...this.menu,
      results,
    };
  }


  get nodeData() {
    return {
      menu: this.menu,
      'type': 'menu',
      metadata: {
        nodeVersion: '1.0.0',
      },
    };
  }
}
